import React from 'react';
import {Link} from '@mui/material';
import WikidataEntityDialog from '../../../../../components/prosemirror-editor/components/WikidataEntityDialog';

// Wrapper component to handle wikidata entity mark with click popover
const WikidataEntityMarkWrapper = ({id, label, url, description, imageUrl, children}) => {
    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <span
                onClick={handleClick}
                style={{
                    color: 'var(--mui-palette-primary-main, #1976d2)',
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    padding: '3px 0 4px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    textDecoration: 'underline dotted',
                    textUnderlineOffset: '2px',
                }}
            >
                {children}
            </span>
            {anchorEl && (
                <WikidataEntityDialog
                    open={true}
                    anchorEl={anchorEl}
                    id={id}
                    label={label}
                    url={url}
                    description={description}
                    imageUrl={imageUrl}
                    onClose={handleClose}
                />
            )}
        </>
    );
};

// Helper: extract the trailing numeric index from nodeKey like "node-3-1" => 1
const getLocalIndex = (nodeKey) => {
    if (typeof nodeKey === 'number') return nodeKey;
    if (!nodeKey) return 0;
    const m = String(nodeKey).match(/-(\d+)$/);
    if (m) return parseInt(m[1], 10);
    if (/^\d+$/.test(String(nodeKey))) return parseInt(nodeKey, 10);
    return 0;
};

const wrapWithLeadingNbsp = (nodeKey, element) => {
    const idx = getLocalIndex(nodeKey);
    if (idx > 0) return <React.Fragment>{'\u00A0'}{element}</React.Fragment>;
    return element;
};

const InlineRenderer = ({node, nodeKey}) => {
    if (!node) return null;

    // Note: hashtag and userMention are now marks (not nodes)
    // They are rendered in the text node marks section below

    // Render inline wikidata mentions from attrs (label + optional url)
    if (node.type === 'wikidataMention') {
        const label = node.attrs?.label ?? '';
        if (!label) return null;
        const url = node.attrs?.url;
        const commonStyle = {
            fontWeight: 600,
            color: 'var(--mui-palette-primary-main, #1976d2)',
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            padding: '3px 0 4px',
            cursor: 'pointer',
        };
        const el = url ? (
            <Link
                key={nodeKey}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                style={commonStyle}
                data-id={node.attrs?.id}
                data-url={url}
                title={label}
            >
                {label}
            </Link>
        ) : (
            <span key={nodeKey} style={commonStyle} data-id={node.attrs?.id} title={label}>
                {label}
            </span>
        );
        return wrapWithLeadingNbsp(nodeKey, el);
    }

    if (node.type === 'text') {
        const marks = node.marks || [];
        let wrapped = node.text ?? '';

        // apply non-reportFragment marks first (e.g., bold)
        marks.forEach((mark) => {
            if (mark.type === 'bold') {
                wrapped = <strong key={`b-${nodeKey}`}>{wrapped}</strong>;
            }
        });

        const rfMark = marks.find(m => m.type === 'reportFragment');
        if (rfMark) {
            const attrs = rfMark.attrs || {};
            const uid = attrs.uid;
            const refNumbers = attrs.refNumbers || '';
            const anchorId = `ref-${uid}`;
            wrapped = (
                <span key={`rf-${nodeKey}`} data-uid={uid} className="inline-ref">
                    {wrapped}
                    <sup style={{marginLeft: 4}}>
                        <Link href={`#${anchorId}`} underline="none">{refNumbers || null}</Link>
                    </sup>
                </span>
            );
        }

        // Handle wikidataEntity mark
        const wikidataEntityMark = marks.find(m => m.type === 'wikidataEntity');
        if (wikidataEntityMark) {
            const attrs = wikidataEntityMark.attrs || {};
            const id = attrs.id;
            const label = attrs.label;
            const url = attrs.url;
            const description = attrs.description || null;
            const imageUrl = attrs.imageUrl || null;

            wrapped = (
                <WikidataEntityMarkWrapper
                    key={`wikidata-${nodeKey}`}
                    id={id}
                    label={label}
                    url={url}
                    description={description}
                    imageUrl={imageUrl}
                >
                    {wrapped}
                </WikidataEntityMarkWrapper>
            );
        }

        // Handle userMention mark
        const userMentionMark = marks.find(m => m.type === 'userMention');
        if (userMentionMark) {
            const attrs = userMentionMark.attrs || {};
            wrapped = (
                <span
                    key={`user-mention-${nodeKey}`}
                    style={{
                        fontWeight: 500,
                        color: '#3d3d3d',
                        backgroundColor: '#e7e7e7',
                        padding: '3px 8px 4px',
                        borderRadius: '12px',
                        display: 'inline-block',
                        lineHeight: 1,
                    }}
                    data-mention-id={attrs.id}
                    data-mention-label={attrs.label}
                >
                    {wrapped}
                </span>
            );
        }

        // Handle hashtag mark
        const hashtagMark = marks.find(m => m.type === 'hashtag');
        if (hashtagMark) {
            const attrs = hashtagMark.attrs || {};
            wrapped = (
                <span
                    key={`hashtag-${nodeKey}`}
                    style={{
                        fontWeight: 500,
                        color: 'var(--mui-palette-secondary-main, #9c27b0)',
                        backgroundColor: 'rgba(156, 39, 176, 0.1)',
                        padding: '3px 8px 4px',
                        borderRadius: '12px',
                        display: 'inline-block',
                        lineHeight: 1,
                    }}
                    data-tag-id={attrs.id}
                    data-tag-label={attrs.label}
                >
                    {wrapped}
                </span>
            );
        }

        // If there's a link mark, wrap the whole thing in an anchor using the mark attrs.
        const linkMark = marks.find(m => m.type === 'link');
        if (linkMark) {
            const attrs = linkMark.attrs || {};
            const href = attrs.href || undefined;
            const target = attrs.target || undefined;
            // Ensure safe rel when opening in a new tab
            const relAttr = attrs.rel || (target === '_blank' ? 'noopener noreferrer' : undefined);

            wrapped = (
                <Link
                    key={`link-${nodeKey}`}
                    href={href}
                    target={target}
                    rel={relAttr}
                    underline="hover"
                    sx={{color: 'primary.main'}}
                >
                    {wrapped}
                </Link>
            );
        }

        return wrapWithLeadingNbsp(nodeKey, <React.Fragment key={nodeKey}>{wrapped}</React.Fragment>);
    }


    // nested nodes
    if (node.content && Array.isArray(node.content)) {
        return (
            <React.Fragment key={nodeKey}>
                {node.content.map((c, i) => (
                    <React.Fragment key={`${nodeKey}-${i}`}>
                        {i > 0 ? '\u00A0' : null}
                        <InlineRenderer node={c} nodeKey={`${nodeKey}-${i}`}/>
                    </React.Fragment>
                ))}
            </React.Fragment>
        );
    }

    return null;
};

export default InlineRenderer;
